// Data hooks of the signature screens (SIGN-FE-01). The lists and the request wrap the generated
// hooks; the request is read again every few seconds while its evidence certificate is being built
// (for a minute at most). A one-time code goes through the plain generated functions, never through
// a mutation: TanStack Query keeps the variables of a mutation in its cache and a code must live
// only in the field and in the request. The 60-second link to the certificate is asked at the click
// and never kept.
import {
  signaturesControllerEvidenceUrl,
  signaturesControllerSign,
  signaturesControllerVerify,
  getSignaturesControllerGetQueryKey,
  useSignaturesControllerCancel,
  useSignaturesControllerCreate,
  useSignaturesControllerDecline,
  useSignaturesControllerGet,
  useSignaturesControllerList,
  useSignaturesControllerSendCode,
  type SignatureRequestDtoOutput,
} from '@batiflow/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  EVIDENCE_POLL_MAX_MS,
  EVIDENCE_POLL_MS,
  type Box,
  type SignatureRequestStatus,
} from './model';

const base = (orgId: string): string => `/api/v1/orgs/${orgId}/signature-requests`;

// --- Lists and one request ---------------------------------------------------------------------

export function useSignatureList(orgId: string, box: Box, status: SignatureRequestStatus | '') {
  return useSignaturesControllerList(
    orgId,
    { box, ...(status ? { status } : {}) },
    { query: { retry: false } },
  );
}

/**
 * One request (404 unless the caller asked for it or has to sign it). While its evidence is
 * `pending` it is read again every few seconds for a minute; `waitedTooLong` then says the
 * certificate is slow, and `restartPolling` starts a new minute.
 */
export function useSignatureRequest(orgId: string, requestId: string) {
  const startedAt = useRef<number | null>(null);
  const [round, setRound] = useState(0);
  const [slow, setSlow] = useState(false);
  const query = useSignaturesControllerGet(orgId, requestId, {
    query: {
      retry: false,
      refetchInterval: (current) => {
        if (current.state.data?.evidence.status !== 'pending') return false;
        const since = startedAt.current;
        return since === null || Date.now() - since < EVIDENCE_POLL_MAX_MS
          ? EVIDENCE_POLL_MS
          : false;
      },
    },
  });
  const pending = query.data?.evidence.status === 'pending';
  // Nothing pending any more: the next wait starts afresh.
  const [wasPending, setWasPending] = useState(false);
  if (pending !== wasPending) {
    setWasPending(pending);
    if (!pending) setSlow(false);
  }
  useEffect(() => {
    if (!pending) {
      startedAt.current = null;
      return undefined;
    }
    startedAt.current = Date.now();
    const timer = setTimeout(() => {
      setSlow(true);
    }, EVIDENCE_POLL_MAX_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [pending, round]);
  return {
    ...query,
    waitedTooLong: pending && slow,
    restartPolling: () => {
      setSlow(false);
      setRound((current) => current + 1);
    },
  };
}

/** Puts a fresh request in the cache and refreshes every list (progress, status). */
export function useApplyRequest(orgId: string) {
  const queryClient = useQueryClient();
  return async (fresh: SignatureRequestDtoOutput): Promise<void> => {
    queryClient.setQueryData(getSignaturesControllerGetQueryKey(orgId, fresh.id), fresh);
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const [key] = query.queryKey;
        return typeof key === 'string' && key === base(orgId);
      },
    });
  };
}

/** Refreshes the lists after a request was made (the new one appears in "Envoyées"). */
export function useRefreshSignatures(orgId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      predicate: (query) => {
        const [key] = query.queryKey;
        return typeof key === 'string' && key.startsWith(base(orgId));
      },
    });
}

// --- Actions -----------------------------------------------------------------------------------

export const useCreateSignatureRequest = useSignaturesControllerCreate;
export const useSendSignatureCode = useSignaturesControllerSendCode;
export const useDeclineSignature = useSignaturesControllerDecline;
export const useCancelSignature = useSignaturesControllerCancel;

/**
 * POST …/sign with the code. Deliberately not a mutation (see the top of the file): the code is an
 * argument of this call and goes nowhere else. The server records the IP address and the browser.
 */
export function signWithCode(
  orgId: string,
  requestId: string,
  code: string,
): Promise<SignatureRequestDtoOutput> {
  return signaturesControllerSign(orgId, requestId, { code });
}

// --- Evidence and verification -----------------------------------------------------------------

/** Asks for the 60-second link to the evidence certificate (409 until it is built). Not cached. */
export function useEvidenceLink(orgId: string) {
  return useMutation({
    mutationKey: ['signature-requests', 'evidence-url', orgId],
    gcTime: 0,
    retry: false,
    mutationFn: (requestId: string) => signaturesControllerEvidenceUrl(orgId, requestId),
  });
}

/** Checks the file and the evidence against their hashes now; the answer is not cached. */
export function useVerifySignature(orgId: string) {
  return useMutation({
    mutationKey: ['signature-requests', 'verify', orgId],
    gcTime: 0,
    retry: false,
    mutationFn: (requestId: string) => signaturesControllerVerify(orgId, requestId),
  });
}

/**
 * Hands the temporary link to the browser as a download: an anchor is clicked and removed, nothing
 * is stored (the link lives 60 seconds and is never put in the app's own URL or storage).
 */
export function saveFromLink(link: { url: string; fileName: string }): void {
  const anchor = document.createElement('a');
  anchor.href = link.url;
  anchor.download = link.fileName;
  anchor.rel = 'noopener noreferrer';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}
